/* Split out of the old monolithic Settings.js so each ตั้งค่า page can be
   rebuilt against its own capture without colliding with the others. */
import { useState } from 'react';
import { settingsApi } from '../../api';
import FormModal from '../../components/FormModal';
import RowActions from '../../components/RowActions';
import { RowMenu, SettingGroup, SettingRow } from '../../components/SettingGroups';
import { IconKey, IconSearch, IconUser } from '../../components/icons';
import { ErrorNote, useLoader } from '../../components/common';
import { SettingsPage, Panel } from './shell';

/**
 * ตั้งค่า · สิทธิ์เข้าใช้.
 *
 * The page is a list of the *people* who can sign in, grouped by the role they
 * hold; the role types themselves live on the second tab. This file used to
 * have it the other way round — a table of roles and no way to see, let alone
 * add, an account.
 */

const TABS = [
  { key: 'users', label: <><IconUser size={16} /> ผู้มีสิทธิ์</> },
  { key: 'types', label: <><IconKey size={16} /> ประเภทสิทธิ์</> },
];

const roleName = (r) => r.name_th || r.name_en;

/** Create and edit ask for the same things; only create sets a password. */
const personFields = (d, withPassword) => [
  { name: 'username', label: 'ชื่อผู้ใช้', required: true },
  { name: 'firstName', label: 'ชื่อจริง' },
  { name: 'lastName', label: 'นามสกุล' },
  { name: 'email', label: 'อีเมล' },
  { name: 'phone', label: 'เบอร์โทรศัพท์' },
  { name: 'roleId', label: 'ประเภทสิทธิ์', type: 'select', required: true,
    options: d.roles.map((r) => ({ value: r.id, label: roleName(r) })) },
  { name: 'branchIds', label: 'สาขาที่เข้าใช้ได้', type: 'multiselect', required: true, wide: true,
    options: d.branches.map((b) => ({ value: b.id, label: b.name_th || b.name_en })) },
  ...(withPassword
    ? [{ name: 'password', label: 'รหัสผ่าน', type: 'password', required: true, wide: true,
        hint: 'อย่างน้อย 8 ตัวอักษร' }]
    : []),
];

const personPayload = (v) => ({
  username: v.username,
  firstName: v.firstName || null,
  lastName: v.lastName || null,
  email: v.email || null,
  phone: v.phone || null,
  roleId: Number(v.roleId),
  branchIds: v.branchIds,
});

export function SettingRole() {
  const [tab, setTab] = useState('users');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [creatingType, setCreatingType] = useState(false);
  const [editingType, setEditingType] = useState(null);

  const people = useLoader(() => settingsApi.users(), []);
  const types = useLoader(() => settingsApi.roles(), []);

  /**
   * The server refuses some of these outright — you cannot retire yourself or
   * the last active owner, and only an owner may touch an owner's password.
   * Its Thai wording says exactly which rule fired, so it is shown as it came
   * back rather than replaced with a guess made here.
   */
  const run = async (fn) => {
    try {
      setError(null);
      await fn();
      people.reload();
    } catch (e) {
      setError(e.message);
    }
  };

  // Role types change what the groups on the first tab are, so both reload.
  const reloadTypes = () => { types.reload(); people.reload(); };

  /* settingsApi.roles() counts user_branch_role rows, which is one per branch —
     a manager on two branches reads as two people. /admin/users counts the
     people, so its number is the one both tabs show. */
  const peopleInRole = (r) =>
    people.data?.roles.find((x) => x.id === r.id)?.user_count ?? r.user_count;

  return (
    <SettingsPage
      tab={tab}
      onTab={setTab}
      tabs={TABS}
      action={tab === 'users' ? 'สร้างผู้มีสิทธิ์เพิ่ม' : 'สร้างประเภทสิทธิ์'}
      onAction={() => (tab === 'users' ? setCreating(true) : setCreatingType(true))}
      search={tab === 'users' ? (
        <label className="search-field">
          <IconSearch size={17} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ค้นหาชื่อ/อีเมล/เบอร์โทร"
          />
        </label>
      ) : null}
    >
      <ErrorNote error={error} />

      {tab === 'users' ? (
        <Panel loader={people} empty={(d) => !d.users.length} emptyIcon="customer">
          {(d) => {
            const q = search.trim().toLowerCase();
            const match = (u) => !q || [
              u.username, u.display_name, u.first_name, u.last_name, u.email, u.phone,
            ].filter(Boolean).join(' ').toLowerCase().includes(q);

            const active = d.users.filter((u) => u.is_active && match(u));
            // Never hidden: an account that created bills is deactivated, not
            // deleted, and the shop still has to be able to find it again.
            const retired = d.users.filter((u) => !u.is_active && match(u));
            const unassigned = active.filter((u) => !u.role_id);

            const rows = (list) => list.map((u) => (
              <SettingRow
                key={u.id}
                label={u.username}
                /* The search box looks at email and phone, so a row found by
                   one of them has to show which. */
                trailing={(u.email || u.phone) && (
                  <span className="text-sm text-gray-400">{u.email || u.phone}</span>
                )}
                menu={(
                  <RowMenu
                    items={[
                      { label: 'แก้ไข', onClick: () => setEditing(u) },
                      { label: 'ตั้งรหัสผ่านใหม่', onClick: () => setResetting(u) },
                      u.is_active
                        ? { label: 'ปิดการใช้งาน', danger: true,
                            onClick: () => run(() => settingsApi.deactivateUser(u.id)) }
                        : { label: 'เปิดใช้งานอีกครั้ง',
                            onClick: () => run(() => settingsApi.updateUser(u.id, { isActive: true })) },
                    ]}
                  />
                )}
              />
            ));

            /* The header count is the server's, so a group can be non-empty and
               still have nothing to draw: everyone in it is retired, or the
               search excluded them. */
            const noRows = (
              <div className="px-4 py-4 text-sm text-gray-400">
                {q ? 'ไม่พบผู้ใช้ที่ค้นหา' : 'ผู้ใช้สิทธิ์นี้ถูกปิดการใช้งานทั้งหมด'}
              </div>
            );

            return (
              /* This page stacks its group cards 8px apart, where ห้องนวด and
                 หมอนวด use 16 — measured on all three. */
              <div className="role-groups">
                {d.roles.map((r) => {
                  const items = rows(active.filter((u) => u.role_id === r.id));
                  return (
                    <SettingGroup
                      key={r.id}
                      title={roleName(r)}
                      count={peopleInRole(r)}
                      countUnit="คน"
                      empty="ยังไม่มีผู้ใช้สิทธิ์นี้"
                    >
                      {items.length ? items : noRows}
                    </SettingGroup>
                  );
                })}

                {/* A user with no user_branch_role row belongs to no group and
                    would otherwise vanish from the only page that lists them. */}
                {unassigned.length > 0 && (
                  <SettingGroup title="ยังไม่ได้กำหนดสิทธิ์" count={unassigned.length} countUnit="คน">
                    {rows(unassigned)}
                  </SettingGroup>
                )}

                <SettingGroup
                  title="ผู้ใช้ที่ปิดการใช้งาน"
                  count={retired.length}
                  countUnit="คน"
                  empty="ไม่มีรายการ"
                >
                  {rows(retired)}
                </SettingGroup>
              </div>
            );
          }}
        </Panel>
      ) : (
        <Panel loader={types} empty={(d) => !d.roles.length} emptyIcon="role">
          {(d) => (
            <div className="card">
              <table className="data">
                <thead>
                  <tr><th>สิทธิ์</th><th className="num">ผู้ใช้</th>
                      <th>สิทธิ์การใช้งาน</th><th>ประเภท</th><th /></tr>
                </thead>
                <tbody>
                  {d.roles.map((r) => (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 600 }}>{roleName(r)}</td>
                      <td className="num">{peopleInRole(r)}</td>
                      <td>
                        {r.permissions.length === 0 ? (
                          <span style={{ color: 'var(--ink-400)' }}>ทั้งหมด</span>
                        ) : (
                          <div className="chip-grid tight">
                            {r.permissions.map((p) => (
                              <span key={p} className="staff-chip small">{p}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${r.is_system ? 'badge-purple' : 'badge-grey'}`}>
                          {r.is_system ? 'ระบบ' : 'กำหนดเอง'}
                        </span>
                      </td>
                      <td>
                        {/* The built-in roles are the app's, not the shop's. */}
                        {!r.is_system && (
                          <RowActions
                            label="สิทธิ์"
                            name={roleName(r)}
                            onEdit={() => setEditingType(r)}
                            onDelete={async () => {
                              await settingsApi.remove('roles', r.id);
                              reloadTypes();
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      )}

      {creating && people.data && (
        <FormModal
          title="สร้างผู้มีสิทธิ์เพิ่ม"
          fields={personFields(people.data, true)}
          onSubmit={async (v) => {
            await settingsApi.createUser({ ...personPayload(v), password: v.password });
            people.reload();
            setCreating(false);
          }}
          onClose={() => setCreating(false)}
        />
      )}

      {editing && people.data && (
        <FormModal
          title={`แก้ไขผู้มีสิทธิ์ — ${editing.username}`}
          fields={personFields(people.data, false)}
          initial={{
            username: editing.username,
            firstName: editing.first_name || '',
            lastName: editing.last_name || '',
            email: editing.email || '',
            phone: editing.phone || '',
            roleId: editing.role_id || '',
            branchIds: editing.branches.map((b) => b.id),
          }}
          onSubmit={async (v) => {
            await settingsApi.updateUser(editing.id, personPayload(v));
            people.reload();
            setEditing(null);
          }}
          onClose={() => setEditing(null)}
        />
      )}

      {resetting && (
        <FormModal
          title={`ตั้งรหัสผ่านใหม่ — ${resetting.username}`}
          submitLabel="ตั้งรหัสผ่าน"
          fields={[
            { name: 'newPassword', label: 'รหัสผ่านใหม่', type: 'password', required: true,
              wide: true, hint: 'อย่างน้อย 8 ตัวอักษร' },
            { name: 'confirm', label: 'ยืนยันรหัสผ่านใหม่', type: 'password', required: true,
              wide: true },
          ]}
          onSubmit={async (v) => {
            // Caught by FormModal and shown in the dialog; the server never sees
            // the confirmation field, so this one check has to happen here.
            if (v.newPassword !== v.confirm) throw new Error('รหัสผ่านทั้งสองช่องไม่ตรงกัน');
            await settingsApi.setUserPassword(resetting.id, v.newPassword);
            setResetting(null);
          }}
          onClose={() => setResetting(null)}
        />
      )}

      {creatingType && (
        <FormModal
          title="สร้างประเภทสิทธิ์"
          fields={[
            { name: 'code', label: 'รหัสสิทธิ์', required: true },
            { name: 'nameEn', label: 'ชื่อ (อังกฤษ)', required: true },
            { name: 'nameTh', label: 'ชื่อ (ไทย)' },
            { name: 'permissions', label: 'สิทธิ์การใช้งาน', type: 'multiselect', wide: true,
              options: (types.data?.permissions || []).map((p) => ({ value: p.code, label: p.code })) },
          ]}
          onSubmit={async (v) => {
            await settingsApi.createRole({
              code: v.code, nameEn: v.nameEn, nameTh: v.nameTh || null,
              permissions: v.permissions || [],
            });
            reloadTypes();
            setCreatingType(false);
          }}
          onClose={() => setCreatingType(false)}
        />
      )}

      {editingType && (
        <FormModal
          title={`แก้ไขประเภทสิทธิ์ — ${roleName(editingType)}`}
          fields={[
            { name: 'code', label: 'รหัสสิทธิ์', required: true },
            { name: 'nameEn', label: 'ชื่อ (อังกฤษ)', required: true },
            { name: 'nameTh', label: 'ชื่อ (ไทย)' },
          ]}
          initial={{
            code: editingType.code,
            nameEn: editingType.name_en,
            nameTh: editingType.name_th || '',
          }}
          onSubmit={async (v) => {
            await settingsApi.update('roles', editingType.id, {
              code: v.code, nameEn: v.nameEn, nameTh: v.nameTh || null,
            });
            reloadTypes();
            setEditingType(null);
          }}
          onClose={() => setEditingType(null)}
        />
      )}
    </SettingsPage>
  );
}
